/**
 * Present the supplied October schedule after an advisory PIN lookup.
 * Shopify/native operations remain authoritative for serviceability, capacity,
 * charges and fulfilment; this script never manipulates purchase controls.
 */
(() => {
  'use strict';

  const monday = /^\d{4}-\d{2}-\d{2}$/;
  const indiaToday = () => new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

  const validMonday = (value) => {
    if (!monday.test(value)) return false;
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.valueOf())
      && date.toISOString().slice(0, 10) === value
      && date.getUTCDay() === 1;
  };

  const fillApprovedFallback = (root, select) => {
    const approvedDays = (root.dataset.approvedDays || '').split(',').map((day) => day.trim()).filter(Boolean);
    const approvedWeeks = (root.dataset.approvedWeeks || '').split(',').map((week) => week.trim()).filter(Boolean);
    if (approvedDays.length === 0 || approvedWeeks.length === 0) return;

    const today = indiaToday();
    const prefix = root.dataset.weekLabel || 'Week of';
    const fmt = new Intl.DateTimeFormat(root.dataset.locale || 'en-IN', {
      timeZone: 'Asia/Kolkata',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const seen = new Set();
    for (const value of approvedWeeks) {
      if (!validMonday(value) || value < today || seen.has(value)) continue;
      seen.add(value);
      const date = new Date(`${value}T12:00:00+05:30`);
      select.add(new Option(`${prefix} ${fmt.format(date)}`, value));
    }
  };

  const districtName = (district) => ({
    kochi: 'Kochi',
    kottayam: 'Kottayam',
    thrissur: 'Thrissur',
  })[district] || district;

  const clearOptions = (select) => {
    while (select.options.length > 1) select.remove(1);
    select.value = '';
    select.disabled = true;
  };

  const setStatus = (status, state, message) => {
    status.dataset.state = state;
    status.textContent = message;
  };

  const formatDate = (value, locale) => new Intl.DateTimeFormat(locale || 'en-IN', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(`${value}T12:00:00+05:30`));

  const weekOf = (cutoffAt) => cutoffAt.slice(0, 10);

  const message = (root, key, fallback) => root.dataset[key] || fallback;

  const eligibleMessage = (root, district) => {
    const name = districtName(district);
    if (district === 'kochi') {
      return message(root, 'kochiEligible', `Good news: we deliver to your area of ${name} every Tuesday.`);
    }
    return message(root, 'districtEligible', 'Good news: we deliver to your area of [district] every Wednesday.')
      .replace('[district]', name);
  };

  const makeChoices = (root, select, options, district, deliveryCount) => {
    const locale = root.dataset.locale || 'en-IN';
    const open = options.filter((option) => option.cutoff_status === 'open');
    // The calendar rolls weekly, so offer the next few start weeks, not all of them.
    const maxStarts = Math.max(1, parseInt(root.dataset.maxStarts, 10) || 4);
    let added = 0;
    for (let index = 0; index < open.length && added < maxStarts; index += 1) {
      const deliveries = open.slice(index, index + deliveryCount);
      if (deliveries.length !== deliveryCount) continue;

      const dates = deliveries.map((entry) => entry.delivery_date);
      const first = deliveries[0];
      const label = deliveryCount === 1
        ? formatDate(first.delivery_date, locale)
        : `${message(root, 'startsLabel', 'Starts')} ${formatDate(first.delivery_date, locale)} · ${dates.map((date) => formatDate(date, locale)).join(', ')}`;
      const option = new Option(label, weekOf(first.cutoff_at));
      option.dataset.deliveryDates = dates.join(', ');
      option.dataset.firstDeliveryDate = first.delivery_date;
      option.dataset.deliveryDay = window.ElaDeliveryRules.rules.districts[district].delivery_weekday;
      select.add(option);
      added += 1;
    }
  };

  const fill = (root) => {
    const select = root.querySelector('[data-ela-weeks]');
    if (!select || root.dataset.ready) return;
    root.dataset.ready = 'true';

    const rulesApi = typeof window !== 'undefined' ? window.ElaDeliveryRules : null;
    if (!rulesApi || typeof rulesApi.findDistrict !== 'function' || typeof rulesApi.selectDeliveryDates !== 'function') {
      fillApprovedFallback(root, select);
      return;
    }

    const pin = root.querySelector('[data-ela-pin]');
    const check = root.querySelector('[data-ela-check]');
    const status = root.querySelector('[data-ela-status]');
    const dateField = root.querySelector('[data-ela-date-field]');
    const dateHint = root.querySelector('[data-ela-date-hint]');
    const districtProperty = root.querySelector('[data-ela-district]');
    const datesProperty = root.querySelector('[data-ela-dates]');
    const dayProperty = root.querySelector('[data-ela-delivery-day]');
    const deliveryCount = Number(root.dataset.deliveryCount);
    const mode = root.dataset.mode;

    if (!pin || !check || !status || !dateField || !dateHint
      || !districtProperty || !datesProperty || !dayProperty) return;

    const clearSelection = () => {
      clearOptions(select);
      dateField.hidden = true;
      dateHint.textContent = '';
      districtProperty.value = '';
      datesProperty.value = '';
      dayProperty.value = '';
    };

    const applySelection = () => {
      const selected = select.options[select.selectedIndex];
      datesProperty.value = selected?.dataset.deliveryDates || '';
      dayProperty.value = selected?.dataset.deliveryDay || '';
    };

    const checkPin = () => {
      clearSelection();
      const location = rulesApi.findDistrict(pin.value);
      if (location.status === 'invalid_input') {
        setStatus(status, 'invalid', message(root, 'invalidPin', 'Enter a complete 6-digit PIN code.'));
        pin.setAttribute('aria-invalid', 'true');
        return;
      }
      pin.removeAttribute('aria-invalid');

      if (location.status !== 'eligible') {
        setStatus(status, 'attention', message(root, 'unlistedPin', 'We don’t deliver to this PIN code yet. WhatsApp us on +91 9745206600 and we’ll let you know when we do.'));
        return;
      }
      if (!Number.isInteger(deliveryCount) || deliveryCount < 1) {
        setStatus(status, 'attention', message(root, 'countUnconfirmed', 'Delivery dates for this plan aren’t open online yet. WhatsApp us on +91 9745206600 to order.'));
        return;
      }

      const schedule = rulesApi.selectDeliveryDates({
        pincode: location.pincode,
        orderedAt: new Date().toISOString(),
      });
      districtProperty.value = districtName(location.district);

      if (schedule.status === 'needs_confirmation') {
        setStatus(status, 'attention', message(root, 'cutoffUnconfirmed', 'Orders for this week are closing right now. Choose the following week, or WhatsApp us to check.'));
        return;
      }
      if (schedule.status !== 'ok') {
        const fourDeliveryNote = deliveryCount === 4
          ? ` ${message(root, 'fourPlanUnavailable', 'Four-delivery plans need four open weeks in a row.')}`
          : '';
        setStatus(status, 'attention', `${message(root, 'noSchedule', 'No delivery dates are open for this plan right now. WhatsApp us and we’ll arrange it.')}${fourDeliveryNote}`);
        return;
      }

      makeChoices(root, select, schedule.options, location.district, deliveryCount);
      if (select.options.length === 1) {
        const fourDeliveryNote = deliveryCount === 4
          ? ` ${message(root, 'fourPlanUnavailable', 'Four-delivery plans need four open weeks in a row.')}`
          : '';
        setStatus(status, 'attention', `${eligibleMessage(root, location.district)}${fourDeliveryNote} ${message(root, 'noCompleteOption', 'No complete set of dates is open right now.')}`);
        return;
      }

      select.disabled = false;
      dateField.hidden = false;
      dateHint.textContent = mode === 'plan' && deliveryCount === 4
        ? message(root, 'fourPlanHint', 'Your boxes arrive on consecutive weeks from the start date you choose.')
        : deliveryCount === 1
          ? message(root, 'optionHintOne', 'Choose the week you want your box.')
          : message(root, 'optionHintMany', 'Each option lists the dates of your [count] deliveries.').replace('[count]', String(deliveryCount));
      setStatus(status, 'eligible', eligibleMessage(root, location.district));
    };

    check.addEventListener('click', checkPin);
    select.addEventListener('change', applySelection);
    pin.addEventListener('input', () => {
      const digits = pin.value.replace(/\D/g, '').slice(0, 6);
      if (pin.value !== digits) pin.value = digits;
      clearSelection();
      pin.removeAttribute('aria-invalid');
      setStatus(status, 'idle', root.dataset.idleStatus || 'Check your PIN code to see your delivery dates.');
    });
    pin.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      checkPin();
    });
  };

  const init = (scope = document) => scope.querySelectorAll('[data-ela-schedule]').forEach(fill);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
